import React, { useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Phone, ArrowRight, Sparkles } from 'lucide-react';
import { trendingCountries, trendingStates, themes } from '../mock';
import SafeImage from '../components/SafeImage';
import OfferRibbon from '../components/OfferRibbon';
import PriceTag from '../components/PriceTag';
import WhatsAppShareBtn from '../components/WhatsAppShareBtn';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePackages } from '../store';

const allDestinations = [...trendingCountries, ...trendingStates];

const CollectionPage = ({ kind }) => {
  // kind: 'destination' | 'theme'
  const { slug } = useParams();
  const nav = useNavigate();
  const { packages: allPackages } = usePackages();

  const item = useMemo(() => {
    const list = kind === 'destination' ? allDestinations : themes;
    return list.find((x) => x.slug === slug);
  }, [kind, slug]);

  const matchedPackages = useMemo(() => {
    if (!slug) return [];
    if (kind === 'destination') {
      // match by explicit destination slug OR by keyword in title/itinerary
      const nameLower = (item?.name || slug).toLowerCase();
      const cityKeywords = {
        sikkim: ['sikkim', 'gangtok', 'pelling', 'lachung', 'darjeeling'],
        kashmir: ['kashmir', 'srinagar', 'gulmarg', 'pahalgam', 'katra', 'vaishno'],
        ladakh: ['leh', 'ladakh', 'nubra', 'pangong'],
        himachal: ['himachal', 'spiti', 'manali', 'shimla', 'kasol', 'tabo', 'narkanda', 'sangla'],
        goa: ['goa'],
        andaman: ['andaman', 'havelock', 'port blair'],
        rajasthan: ['rajasthan', 'jaipur', 'udaipur', 'jodhpur', 'jaisalmer'],
        kerala: ['kerala', 'munnar', 'alleppey', 'kochi', 'varkala'],
        thailand: ['thailand', 'phuket', 'krabi', 'bangkok', 'pattaya', 'koh'],
        indonesia: ['bali', 'indonesia', 'ubud', 'nusa', 'kuta'],
        vietnam: ['vietnam', 'hanoi', 'ha long', 'da nang', 'hoi an', 'ho chi minh'],
        singapore: ['singapore'],
        malaysia: ['malaysia', 'kuala lumpur', 'langkawi'],
        bhutan: ['bhutan', 'thimphu', 'paro', 'punakha'],
        maldives: ['maldives', 'male'],
        uae: ['dubai', 'abu dhabi', 'uae', 'emirates'],
      };
      const keys = cityKeywords[slug] || [nameLower];
      return allPackages.filter((p) => {
        if (p.destination === slug) return true;
        const hay = (p.title + ' ' + p.itinerary).toLowerCase();
        return keys.some((k) => hay.includes(k));
      });
    }
    // theme
    return allPackages.filter((p) => (p.themes || []).includes(slug));
  }, [kind, slug, item, allPackages]);

  if (!item) {
    return (
      <div className="min-h-screen">
        <div className="bg-[#0d64a8] h-24" />
        <div className="max-w-lg mx-auto py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{kind === 'destination' ? 'Destination' : 'Theme'} not found</h2>
          <p className="text-gray-600 mb-6">The page you are looking for may have been moved.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0d64a8] text-white px-5 py-2.5 rounded-md">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const heroLabel = kind === 'destination' ? 'Destination' : 'Theme';

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0d64a8]">
        <Header />
        <div className="h-24" />
      </div>

      {/* Hero */}
      <section className="relative h-[460px] overflow-hidden">
        <SafeImage src={item.image} alt={item.name} fallbackType={item.fbType} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute inset-0 max-w-[1280px] mx-auto px-5 lg:px-8 flex flex-col justify-end pb-12">
          <button onClick={() => nav(-1)} className="absolute top-6 left-5 lg:left-8 flex items-center gap-1.5 text-white/90 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-[#ffcc29] text-[#0a2a4a] px-2.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider">{heroLabel}</span>
            <span className="text-white/80 text-xs">{matchedPackages.length} package{matchedPackages.length === 1 ? '' : 's'} available</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow max-w-4xl leading-tight">
            {kind === 'destination' ? `Explore ${item.name}` : item.name}
          </h1>
          {item.tagline && (
            <p className="text-lg text-[#ffcc29] font-medium mt-2">{item.tagline}</p>
          )}
          {item.blurb && (
            <p className="text-white/85 text-base mt-3 max-w-3xl leading-relaxed">{item.blurb}</p>
          )}
        </div>
      </section>

      {/* Packages */}
      <section className="py-12">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                {kind === 'destination' ? `${item.name} Packages` : `${item.name} Trips`}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Handpicked itineraries, curated by travellers who have been there.</p>
            </div>
            <a href="tel:+919650336344" className="flex items-center gap-1.5 text-sm text-[#0d64a8] font-medium border border-[#0d64a8]/20 hover:bg-[#e8f2fb] px-4 py-2 rounded-md transition">
              <Phone size={14} /> Talk to an expert
            </a>
          </div>

          {matchedPackages.length === 0 ? (
            <div className="bg-[#f7fafc] border border-gray-100 rounded-xl p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-[#0d64a8]/10 text-[#0d64a8] flex items-center justify-center mx-auto mb-3">
                <Sparkles size={22} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Curated packages coming soon</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mt-1">
                We are building tailor-made itineraries for {item.name}. Meanwhile, our travel experts can craft a custom trip for you \u2014 usually within 24 hours.
              </p>
              <Link to="/" className="inline-flex items-center gap-1.5 mt-5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-2.5 rounded-md">
                Request a custom quote <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matchedPackages.map((p) => (
                <Link
                  to={`/package/${p.id}`}
                  key={p.id}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative">
                    <SafeImage src={p.image} alt={p.title} fallbackType={p.fbType || 'default'} className="w-full h-52 object-cover" />
                    {p.offer_enabled && (
                      <OfferRibbon text={p.offer_ribbon_text || 'Limited Offer'} endsAt={p.offer_ends_at} />
                    )}
                    {(() => {
                      const themeRaw = Array.isArray(p.themes) && p.themes.length > 0
                        ? p.themes[0]
                        : (p.category === 'india' ? 'India' : 'International');
                      const label = String(themeRaw).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      return (
                        <div className="absolute top-3 right-3 bg-[#0d64a8] text-white text-[11px] font-medium px-2.5 py-1 rounded shadow-sm capitalize">
                          {label}
                        </div>
                      );
                    })()}
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white border border-gray-100 shadow px-4 py-2 rounded-md flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                      <Clock size={14} className="text-[#0d64a8]" /> {p.duration}
                    </div>
                  </div>
                  <div className="pt-8 px-5 pb-5">
                    <h3 className="text-[17px] font-semibold text-gray-900 mb-3 line-clamp-2 min-h-[48px] group-hover:text-[#0d64a8] transition">{p.title}</h3>
                    <div className="flex items-start gap-1.5 text-sm text-gray-600 mb-4">
                      <MapPin size={14} className="text-[#0d64a8] mt-0.5 shrink-0" />
                      <span className="line-clamp-1">{p.itinerary}</span>
                    </div>
                    <div className="h-px bg-gray-100 my-3" />
                    <div className="flex items-end justify-between gap-2">
                      <PriceTag
                        price={p.price}
                        offerEnabled={p.offer_enabled}
                        offerPrice={p.offer_price}
                        size="md"
                        suffix="/ Adult"
                      />
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.offer_enabled && Number(p.offer_price) > 0 && Number(p.offer_price) < Number(p.price || 0) && (
                          <WhatsAppShareBtn pkg={p} variant="card" />
                        )}
                        <span className="bg-[#0d64a8] text-white text-xs font-medium px-3 py-2 rounded">View Details</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related discovery */}
      <section className="py-12 bg-[#f7fafc] border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {kind === 'destination' ? 'Browse more themes' : 'Explore destinations'}
          </h3>
          <div className="flex flex-wrap gap-2">
            {(kind === 'destination' ? themes : allDestinations).map((x) => (
              <Link
                key={x.slug}
                to={`/${kind === 'destination' ? 'theme' : 'destination'}/${x.slug}`}
                className="bg-white border border-gray-200 hover:border-[#0d64a8] hover:text-[#0d64a8] rounded-full px-4 py-1.5 text-sm text-gray-700 transition"
              >
                {x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CollectionPage;
